import { AxiosError } from "axios";
import { z } from "zod";
import { KeyValue } from "@/main/models/common";
import { PaginationResponse } from "@/main/models/common/paginationResponse";
import type { SearchTermRequest } from "@/main/models/common/searchTermRequest";
import { AutocompleteBodyshopAndSupplierRequest, VoucherSearchGridRequest } from "@/main/models/request";
import { VoucherSearchGridItemResponse, VoucherSearchGridResponse } from "@/main/models/response";
import { DefaultValueParameterEnum } from "@/main/services/enum/DefaultValueParameterEnum";
import { HttpStatusEnum } from "@/main/services/enum/HttpStatusEnum";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { VoucherRefundStatusEnum } from "@/main/services/enum/VoucherRefundStatusEnum";
import { VoucherTypeEnum } from "@/main/services/enum/VoucherTypeEnum";
import { getTranslateByKey } from "@/main/services/utils";
import { finalDateForm, initalDateForm } from "@/main/services/utilsDate";

export const REFUNDS_RANGE_DAYS = DefaultValueParameterEnum.FallbackRangeDate;
export const EMPTY_VALUE = "--";

export type RefundsPage = PaginationResponse<VoucherSearchGridItemResponse>;

export const EMPTY_REFUNDS_PAGE: RefundsPage = {
	isHideToastNotification: true,
	pageNumber: PaginationEnum.PageIndexIntial,
	pageSize: PaginationEnum.PageSize30,
	totalNumberOfPages: 0,
	totalNumberOfRecords: 0,
	results: [],
};

const requiredMsg = (fieldKey: string) =>
	getTranslateByKey("information.isRequired", true).replace("{0}", getTranslateByKey(fieldKey));

export const searchRefundsSchema = z
	.object({
		claimNumber: z.string().nullable(),
		voucherCode: z.string().nullable(),
		startDate: z.date().nullable(),
		finalDate: z.date().nullable(),
		insurer: z.custom<KeyValue>().nullable(),
		company: z.custom<KeyValue>().nullable(),
		refundType: z.custom<KeyValue>().nullable(),
		voucherType: z.custom<KeyValue>().nullable(),
	})
	.superRefine((data, ctx) => {
		if (!data.refundType) {
			ctx.addIssue({
				code: "custom",
				path: ["refundType"],
				message: requiredMsg("information.refundType"),
			});
		}
	});

export type ISearchRefundsForm = z.infer<typeof searchRefundsSchema>;

export function getInitialSearchRefundsForm(): ISearchRefundsForm {
	return {
		claimNumber: null,
		voucherCode: null,
		startDate: initalDateForm(REFUNDS_RANGE_DAYS),
		finalDate: finalDateForm(),
		insurer: null,
		company: null,
		refundType: null,
		voucherType: null,
	};
}

export function getSingleOption(options: KeyValue[] | null): KeyValue | null {
	return options?.length === 1 ? options[0] : null;
}

export function getPendingRefundTypeOption(options: KeyValue[] | null): KeyValue | null {
	return options?.find((option) => String(option.key) === String(VoucherRefundStatusEnum.Pending)) ?? null;
}

export function getDefaultVoucherTypeOption(options: KeyValue[] | null, isSupplier: boolean): KeyValue | null {
	if (!isSupplier) return getSingleOption(options);
	return options?.find((option) => String(option.key) === String(VoucherTypeEnum.ReplacementParts)) ?? null;
}

function optionalText(value: string | null): string | undefined {
	return value?.trim() || undefined;
}

export function buildRefundSearchRequest(form: ISearchRefundsForm): VoucherSearchGridRequest {
	return {
		pageIndex: PaginationEnum.PageIndexIntial,
		pageSize: PaginationEnum.PageSize30,
		idVoucherRefundStatus: form.refundType ? Number(form.refundType.key) : undefined,
		creationDateSince: form.startDate?.toISOString(),
		creationDateUntil: form.finalDate?.toISOString(),
		claimReportNumber: optionalText(form.claimNumber),
		voucherCode: optionalText(form.voucherCode),
		guidInsurer: form.insurer ? String(form.insurer.key) : undefined,
		guidCompany: form.company ? String(form.company.key) : undefined,
		idVoucherType: form.voucherType ? Number(form.voucherType.key) : undefined,
	};
}

export function buildCompanyAutocompleteRequest(
	{ searchTerm, pageIndex, pageSize }: SearchTermRequest,
	guidInsurer: string,
): AutocompleteBodyshopAndSupplierRequest {
	return { name: searchTerm, guidInsurer: guidInsurer || undefined, pageIndex, pageSize };
}

export function toRefundsPage(
	response: VoucherSearchGridResponse | null,
	request: VoucherSearchGridRequest,
): RefundsPage {
	const pageSize = request.pageSize ?? PaginationEnum.PageSize30;
	const totalRecords = response?.totalRecords ?? 0;

	return {
		isHideToastNotification: false,
		pageNumber: request.pageIndex ?? PaginationEnum.PageIndexIntial,
		pageSize,
		totalNumberOfPages: Math.ceil(totalRecords / pageSize),
		totalNumberOfRecords: totalRecords,
		results: response?.items ?? [],
	};
}

export function getRefundTotalLabel(idVoucherRefundStatus: number | undefined): string {
	return getTranslateByKey(
		idVoucherRefundStatus === VoucherRefundStatusEnum.Applied
			? "information.totalRefunded"
			: "information.totalPendingRefunds",
	);
}

export function isClaimsUnavailableError(error: unknown): boolean {
	return error instanceof AxiosError && error.response?.status === HttpStatusEnum.ServiceUnavailable;
}

export function textOrEmptyValue(value: string | null | undefined): string {
	return value?.trim() ? value : EMPTY_VALUE;
}
